import React, { useState, useEffect, useCallback } from 'react';

const STALE_MS = 30 * 60 * 1000;

const TRADING_HOURS = {
    NSE: { start: 9 * 60,  end: 15 * 60 + 30 },
    BSE: { start: 9 * 60,  end: 15 * 60 + 30 },
    MCX: { start: 9 * 60,  end: 23 * 60 + 59 },
};

const EX_STYLE = {
    NSE: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    BSE: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
    MCX: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
};

const ST_STYLE = {
    SUCCESS: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0', label: 'Success' },
    WARNING: { bg: '#fffbeb', color: '#92400e', border: '#fde68a', label: 'Warning' },
    ERROR:   { bg: '#fef2f2', color: '#991b1b', border: '#fecaca', label: 'Error'   },
};

const FEEDS = [
    { exchange: 'NSE', segment: 'CM', source: 'Trade_NSE_CM', label: 'NSE Cash' },
    { exchange: 'NSE', segment: 'FO', source: 'Trade_NSE_FO', label: 'NSE F&O'  },
    { exchange: 'BSE', segment: 'CM', source: 'Trade_BSE_CM', label: 'BSE Cash' },
    { exchange: 'BSE', segment: 'FO', source: 'Trade_BSE_FO', label: 'BSE F&O'  },
    { exchange: 'MCX', segment: 'FO', source: 'Trade_MCX_FO', label: 'MCX F&O'  },
];

const TODAY = new Date().toISOString().slice(0, 10);

function isInTradingHours(exchange) {
    const now  = new Date();
    const ist  = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
    const mins = ist.getUTCHours() * 60 + ist.getUTCMinutes();
    const h    = TRADING_HOURS[exchange] || TRADING_HOURS.NSE;
    return mins >= h.start && mins <= h.end;
}

function fmtDT(ts) {
    if (!ts) return '-';
    return new Date(ts).toLocaleString('en-IN', {
        day: '2-digit', month: 'short',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
}

function timeSince(ts) {
    if (!ts) return null;
    const ms = Date.now() - new Date(ts).getTime();
    if (ms < 60000)   return Math.floor(ms / 1000) + 's ago';
    if (ms < 3600000) return Math.floor(ms / 60000) + 'm ago';
    return Math.floor(ms / 3600000) + 'h ago';
}

function authH() {
    return {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + (localStorage.getItem('nb_admin_token') || ''),
    };
}

export default function SyncMonitor() {
    const [status,    setStatus]    = useState([]);
    const [logs,      setLogs]      = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [lastFetch, setLastFetch] = useState(null);
    const [filter,    setFilter]    = useState({
        exchange: 'ALL',
        status:   'ALL',
        dateFrom: TODAY,
        dateTo:   TODAY,
    });
    const [page,       setPage]       = useState(1);
    const [pagination, setPagination] = useState(null);
    const [summary,    setSummary]    = useState(null);

    const load = useCallback(async (pageOverride) => {
        const effectivePage = pageOverride || page;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filter.dateFrom) params.append('date_from', filter.dateFrom);
            if (filter.dateTo)   params.append('date_to',   filter.dateTo);
            if (filter.exchange !== 'ALL') params.append('exchange', filter.exchange);
            if (filter.status   !== 'ALL') params.append('status',   filter.status);
            params.append('page',  effectivePage);
            params.append('limit', 200);

            const [statusRes, logsRes] = await Promise.all([
                fetch('/api/dropcopy/status',              { headers: authH() }),
                fetch('/api/dropcopy/sync-logs?' + params, { headers: authH() }),
            ]);

            const statusData = statusRes.ok ? await statusRes.json() : { status: [] };
            const logsData   = logsRes.ok  ? await logsRes.json()   : { logs: [], summary: null, pagination: null };

            setStatus(statusData.status || []);
            setLogs(logsData.logs       || []);
            // summary/pagination reflect the WHOLE filtered range from the backend,
            // not just the current page -- see the /sync-logs route fix.
            setSummary(logsData.summary       || null);
            setPagination(logsData.pagination || null);
            setLastFetch(new Date());
        } catch (e) {
            console.error('[SyncMonitor]', e);
        } finally {
            setLoading(false);
        }
    }, [filter, page]);

    // Initial load
    useEffect(() => { load(); }, []); // eslint-disable-line

    // Auto-refresh every 60s
    useEffect(() => {
        const iv = setInterval(() => load(), 60000);
        return () => clearInterval(iv);
    }, [load]);

    // Feed health cards
    const feedSummary = FEEDS.map(feed => {
        const statusRow = status.find(s =>
            s.exchange === feed.exchange && s.segment === feed.segment
        );
        const feedLogs  = logs.filter(l => l.file_source === feed.source);
        const lastLog   = feedLogs[0] || null;
        const lastOk    = feedLogs.find(l => l.status === 'SUCCESS') || null;
        const inHours   = isInTradingHours(feed.exchange);
        const staleMs   = lastOk ? Date.now() - new Date(lastOk.log_time).getTime() : Infinity;
        const isStale   = inHours && staleMs > STALE_MS;
        const hasError  = lastLog?.status === 'ERROR';

        let health = 'OK';
        if (!lastOk && !statusRow)         health = inHours ? 'NO_DATA' : 'IDLE';
        else if (hasError)                 health = 'ERROR';
        else if (isStale)                  health = 'STALE';

        return { ...feed, statusRow, lastLog, lastOk, health, inHours, staleMs };
    });

    const alertCount  = feedSummary.filter(f => f.health === 'ERROR' || f.health === 'STALE' || f.health === 'NO_DATA').length;
    // FIX (16-Jul-2026): these used to be derived from `logs` (the current
    // page only, capped at 200 by the old query) so "Total: 200 / Success: 200"
    // was really just "how many of THIS page's rows are Success" -- misleading
    // once a day's real sync count exceeds one page. Now sourced from the
    // backend's `summary`, which counts across the WHOLE filtered date range
    // regardless of pagination.
    const totalEvents = summary ? summary.total          : logs.length;
    const errorCount  = summary ? summary.error_count    : logs.filter(l => l.status === 'ERROR').length;
    const successCount= summary ? summary.success_count  : logs.filter(l => l.status === 'SUCCESS').length;

    const s = {
        card:   { background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 16 },
        th:     { background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0', color: '#64748b', fontSize: 10, fontWeight: 600, padding: '8px 14px', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' },
        td:     { color: '#0d0f12', fontSize: 12, padding: '10px 14px', verticalAlign: 'middle', borderBottom: '0.5px solid #f1f5f9' },
        sel:    { padding: '6px 10px', borderRadius: 6, border: '0.5px solid #e2e8f0', background: '#f8fafc', fontSize: 12, color: '#0d0f12', outline: 'none', cursor: 'pointer' },
        input:  { padding: '6px 10px', borderRadius: 6, border: '0.5px solid #e2e8f0', background: '#f8fafc', fontSize: 12, color: '#0d0f12', outline: 'none' },
        btn:    { background: '#eff6ff', border: '0.5px solid #bfdbfe', borderRadius: 6, padding: '6px 16px', fontSize: 12, color: '#1d4ed8', cursor: 'pointer', fontFamily: 'inherit' },
    };

    return (
        <div style={{ fontFamily: "'DM Sans',-apple-system,sans-serif" }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#0d0f12' }}>DropCopy Sync Monitor</div>
                    {alertCount > 0
                        ? <span style={{ background: '#fef2f2', color: '#dc2626', border: '0.5px solid #fecaca', borderRadius: 6, fontSize: 11, fontWeight: 700, padding: '2px 10px' }}>
                            {alertCount} ALERT{alertCount > 1 ? 'S' : ''}
                          </span>
                        : !loading && <span style={{ background: '#f0fdf4', color: '#15803d', border: '0.5px solid #bbf7d0', borderRadius: 6, fontSize: 11, fontWeight: 600, padding: '2px 10px' }}>
                            All feeds healthy
                          </span>
                    }
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {lastFetch && <span style={{ fontSize: 11, color: '#94a3b8' }}>Updated {timeSince(lastFetch)} &middot; auto-refreshes every 60s</span>}
                    <button onClick={load} disabled={loading} style={{ ...s.btn, opacity: loading ? 0.6 : 1 }}>
                        {loading ? 'Loading...' : 'Refresh now'}
                    </button>
                </div>
            </div>

            {/* Feed health cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 20 }}>
                {feedSummary.map(feed => {
                    const ex = EX_STYLE[feed.exchange] || EX_STYLE.NSE;
                    const isAlert = feed.health === 'ERROR' || feed.health === 'STALE' || feed.health === 'NO_DATA';
                    const hc = { OK: '#15803d', IDLE: '#94a3b8', NO_DATA: '#d97706', STALE: '#d97706', ERROR: '#dc2626' }[feed.health] || '#94a3b8';
                    const hl = { OK: 'Syncing', IDLE: 'Idle (off hours)', NO_DATA: 'No data', STALE: 'Stale', ERROR: 'Error' }[feed.health] || feed.health;
                    return (
                        <div key={feed.source} style={{ background: '#fff', border: `0.5px solid ${isAlert ? hc : '#e2e8f0'}`, borderRadius: 10, padding: '13px 15px', boxShadow: isAlert ? `0 0 0 2px ${hc}22` : 'none' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: ex.bg, color: ex.color, border: `0.5px solid ${ex.border}` }}>
                                    {feed.exchange} {feed.segment}
                                </span>
                                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: `${hc}15`, color: hc }}>
                                    {hl}
                                </span>
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#0d0f12', marginBottom: 4 }}>{feed.label}</div>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>
                                {feed.statusRow
                                    ? <>{feed.statusRow.position_count?.toLocaleString()} positions &middot; {feed.statusRow.client_count} clients</>
                                    : <span style={{ color: '#94a3b8' }}>No positions today</span>
                                }
                            </div>
                            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                                {feed.lastOk
                                    ? 'Last sync: ' + timeSince(feed.lastOk.log_time) + (feed.lastOk.source_host ? ' · ' + feed.lastOk.source_host : '')
                                    : 'No successful sync today'}
                            </div>
                            {feed.inHours && feed.health === 'STALE' && (
                                <div style={{ marginTop: 6, fontSize: 10, color: '#d97706', background: '#fffbeb', border: '0.5px solid #fde68a', borderRadius: 4, padding: '3px 7px' }}>
                                    No sync for {Math.floor(feed.staleMs / 60000)}m
                                </div>
                            )}
                            {feed.health === 'ERROR' && feed.lastLog?.error_message && (
                                <div style={{ marginTop: 6, fontSize: 10, color: '#dc2626', background: '#fef2f2', border: '0.5px solid #fecaca', borderRadius: 4, padding: '3px 7px' }}>
                                    {feed.lastLog.error_message.slice(0, 60)}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Sync Log table */}
            <div style={s.card}>
                {/* Filters row -- same pattern as existing admin pages */}
                <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#f8fafc' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#0d0f12' }}>Sync Log</span>

                    <input type="date" value={filter.dateFrom}
                        onChange={e => setFilter(f => ({ ...f, dateFrom: e.target.value }))}
                        style={s.input} title="From date" />
                    <input type="date" value={filter.dateTo}
                        onChange={e => setFilter(f => ({ ...f, dateTo: e.target.value }))}
                        style={s.input} title="To date" />

                    <select value={filter.exchange} onChange={e => setFilter(f => ({ ...f, exchange: e.target.value }))} style={s.sel}>
                        <option value="ALL">All exchanges</option>
                        <option value="NSE">NSE</option>
                        <option value="BSE">BSE</option>
                        <option value="MCX">MCX</option>
                    </select>
                    <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))} style={s.sel}>
                        <option value="ALL">All statuses</option>
                        <option value="SUCCESS">Success</option>
                        <option value="WARNING">Warning</option>
                        <option value="ERROR">Error</option>
                    </select>

                    <button onClick={() => { setPage(1); load(1); }} disabled={loading} style={{ ...s.btn, opacity: loading ? 0.6 : 1 }}>
                        Apply
                    </button>

                    {/* Summary counts */}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: '#64748b' }}>Total: <strong>{totalEvents}</strong></span>
                        <span style={{ fontSize: 11, color: '#15803d' }}>Success: <strong>{successCount}</strong></span>
                        {errorCount > 0 && (
                            <span style={{ fontSize: 11, color: '#dc2626' }}>Errors: <strong>{errorCount}</strong></span>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading sync logs...</div>
                ) : logs.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No sync logs found for the selected filters.</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                            <thead>
                                <tr>
                                    {['Time', 'Exchange', 'File Source', 'Source Host', 'Status', 'Trades', 'Positions', 'Duration', 'Error'].map(h => (
                                        <th key={h} style={s.th}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log, i) => {
                                    const st = ST_STYLE[log.status] || ST_STYLE.SUCCESS;
                                    const ex = EX_STYLE[log.exchange] || EX_STYLE.NSE;
                                    return (
                                        <tr key={log.id || i}>
                                            <td style={{ ...s.td, whiteSpace: 'nowrap', fontSize: 11 }}>{fmtDT(log.log_time)}</td>
                                            <td style={s.td}>
                                                <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: ex.bg, color: ex.color, border: `0.5px solid ${ex.border}` }}>
                                                    {log.exchange} {log.segment}
                                                </span>
                                            </td>
                                            <td style={{ ...s.td, fontSize: 11, color: '#64748b' }}>{log.file_source}</td>
                                            <td style={{ ...s.td, fontSize: 11, color: '#64748b' }}>{log.source_host || '—'}</td>
                                            <td style={s.td}>
                                                <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: st.bg, color: st.color, border: `0.5px solid ${st.border}` }}>
                                                    {st.label}
                                                </span>
                                            </td>
                                            <td style={{ ...s.td, textAlign: 'right' }}>{(log.trades_count || 0).toLocaleString()}</td>
                                            <td style={{ ...s.td, textAlign: 'right' }}>{(log.positions_count || 0).toLocaleString()}</td>
                                            <td style={{ ...s.td, color: '#64748b', fontSize: 11 }}>
                                                {log.sync_duration_ms ? (log.sync_duration_ms / 1000).toFixed(1) + 's' : '-'}
                                            </td>
                                            <td style={{ ...s.td, fontSize: 10, color: '#dc2626', maxWidth: 200 }}>
                                                {log.error_message || '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination -- only the table body is paginated; feed cards
                    and Total/Success/Errors above always reflect the full
                    filtered range regardless of which page is shown here. */}
                {pagination && pagination.total_pages > 1 && (
                    <div style={{ padding: '10px 16px', borderTop: '0.5px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, background: '#f8fafc' }}>
                        <span style={{ fontSize: 11, color: '#64748b' }}>
                            Page {pagination.page} of {pagination.total_pages} &middot; {pagination.total} total events
                        </span>
                        <button style={{ ...s.btn, opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1}
                            onClick={() => { const p = Math.max(1, page - 1); setPage(p); load(p); }}>&larr; Prev</button>
                        <button style={{ ...s.btn, opacity: !pagination.has_next ? 0.5 : 1 }} disabled={!pagination.has_next}
                            onClick={() => { const p = page + 1; setPage(p); load(p); }}>Next &rarr;</button>
                    </div>
                )}
            </div>
        </div>
    );
}